/**
 * Configuración de la aplicación cargada en tiempo de ejecución
 * Este archivo permite cambiar la configuración sin recompilar la aplicación
 */

import { devError, devLog } from '../utils/devLog';

export interface AppConfig {
  apiUrl: string;
  siteName?: string;
  clarityProjectId?: string;
}

let appConfig: AppConfig | null = null;

const isDevelopmentMode = (): boolean =>
  process.env.NODE_ENV === 'development' ||
  process.env.REACT_APP_ENV === 'development';

const buildDevConfig = (): AppConfig => {
  const apiUrl = process.env.REACT_APP_API_URL?.trim();
  if (!apiUrl) {
    console.warn(
      'REACT_APP_API_URL no está definida en .env.development; usando proxy CRA (peticiones relativas /api → http://localhost:5000).',
    );
  }
  return {
    // Vacío = mismo origen; setupProxy.js reenvía /api a la API en desarrollo.
    apiUrl: apiUrl || '',
    siteName: process.env.REACT_APP_SITE_NAME || 'Neos One',
    clarityProjectId: process.env.REACT_APP_CLARITY_PROJECT_ID || undefined,
  };
};

/**
 * Carga la configuración de la app.
 * - Desarrollo (`npm start`): apiUrl solo desde `.env.development` (REACT_APP_API_URL).
 * - Producción: `public/config.json` en runtime (multi-sitio sin recompilar).
 */
export const loadAppConfig = async (): Promise<AppConfig> => {
  if (appConfig) {
    return appConfig;
  }

  if (isDevelopmentMode()) {
    appConfig = buildDevConfig();
    console.log('✅ Configuración de desarrollo (.env.development):', appConfig);
    return appConfig;
  }

  try {
    // Intentar cargar la configuración desde el archivo config.json
    const response = await fetch('/config.json');
    if (!response.ok) {
      throw new Error(`config.json no encontrado (${response.status})`);
    }
    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) {
      throw new Error('config.json no encontrado (el servidor devolvió HTML en lugar de JSON)');
    }
    const config = await response.json();
    
    // Validar que tenga la URL de la API
    if (!config.apiUrl) {
      throw new Error('config.json debe contener la propiedad apiUrl');
    }

    appConfig = {
      apiUrl: config.apiUrl,
      siteName: config.siteName || 'Neos One',
      clarityProjectId: config.clarityProjectId || undefined,
    };

    devLog('✅ Configuración cargada:', appConfig);
    return appConfig;
  } catch (error) {
    devError('❌ Error al cargar config.json, usando valores por defecto:', error);

    appConfig = {
      apiUrl: process.env.REACT_APP_API_URL || 'https://localhost:3000',
      siteName: process.env.REACT_APP_SITE_NAME || 'Neos One',
    };
    
    return appConfig;
  }
};

/**
 * Obtiene la configuración de la aplicación
 * Si no está cargada, la carga primero
 */
export const getAppConfig = async (): Promise<AppConfig> => {
  return await loadAppConfig(); // Retorna la configuración cargada
};

/**
 * Obtiene la URL de la API de forma síncrona (solo si ya fue cargada)
 * Útil para casos donde no se puede usar async/await
 */
export const getApiUrl = (): string => {
  if (isDevelopmentMode()) {
    return process.env.REACT_APP_API_URL?.trim() || '';
  }
  if (appConfig) {
    return appConfig.apiUrl;
  }
  return process.env.REACT_APP_API_URL || 'https://localhost:3000';
};

/**
 * Obtiene el nombre del sitio de forma síncrona (solo si ya fue cargada)
 * Útil para casos donde no se puede usar async/await
 */
export const getSiteName = (): string => {
  if (appConfig) {
    return appConfig.siteName || 'Neos One';
  }
  // Fallback a variable de entorno o valor por defecto
  return process.env.REACT_APP_SITE_NAME || 'Neos One';
};


